import React from 'react';
import { DeviceTypeId } from '../../../constants/deviceTypes';
import { isPowerMetric, isFermentorMetric } from '../../../constants/metricConfig';
import { CameraRenderer, SensorRenderer, MixerRenderer, extractPowerMetrics } from './renderers';

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  deviceTypes?: {
    id: number;
    name: string;
    icon?: string;
  };
  settings?: {
    cameraStreamEndpoint?: string;
    [key: string]: any;
  };
  connectedTo?: number;
}

interface DeviceRendererProps {
  /** Device to render */
  device: Device;
  /** Metrics for this device */
  metrics: Record<string, any[]>;
  /** Metrics from child devices (keyed by child device ID) */
  childMetrics?: Record<number, Record<string, any[]>>;
  /** Child devices connected to this device */
  childDevices?: Device[];
  /** Online status (0 or 1) */
  online: 0 | 1;
  /** Theme color */
  themeColor?: string;
  /** Device display name */
  deviceName?: string;
  /** Callback for actions */
  onAction?: (action: string, device: Device) => void;
}

type DeviceCapability = 'MIXER' | 'FERMENTOR' | 'QA' | 'CAMERA' | 'SENSOR' | 'GENERIC';

/**
 * Detect device capability based on metrics and child devices
 */
function detectDeviceCapability(
  device: Device,
  metrics: Record<string, any[]>,
  childMetrics: Record<number, Record<string, any[]>>,
  childDevices: Device[]
): DeviceCapability {
  // Camera devices (including stereo/RealSense) render their live color stream
  if (device.deviceTypes?.id === DeviceTypeId.CAMERA ||
      device.deviceTypes?.id === DeviceTypeId.STEREO_CAMERA) {
    return 'CAMERA';
  }

  // Collect all metric keys from this device and children
  const allMetricKeys = new Set<string>();

  Object.keys(metrics).forEach(k => allMetricKeys.add(k));
  Object.values(childMetrics).forEach(m => {
    Object.keys(m).forEach(k => allMetricKeys.add(k));
  });

  // Check for power/mixer capability
  const hasPowerMetrics = Array.from(allMetricKeys).some(isPowerMetric);
  if (hasPowerMetrics) {
    return 'MIXER';
  }

  // Check for fermentor/bioprocess capability
  const hasFermentorMetrics = Array.from(allMetricKeys).some(isFermentorMetric);
  if (hasFermentorMetrics) {
    return 'FERMENTOR';
  }

  // Check for camera child (QA station)
  const hasCameraChild = childDevices.some(
    child => child.deviceTypes?.id === DeviceTypeId.CAMERA
  );
  if (hasCameraChild) {
    return 'QA';
  }

  // PLC or USB devices are sensors
  if (device.deviceTypes?.id === DeviceTypeId.PLC ||
      device.deviceTypes?.id === DeviceTypeId.USB) {
    return 'SENSOR';
  }

  return 'GENERIC';
}

/**
 * Routes to the appropriate renderer based on device type and capabilities
 */
const DeviceRenderer: React.FC<DeviceRendererProps> = ({
  device,
  metrics,
  childMetrics = {},
  childDevices = [],
  online,
  themeColor = '#53bde4',
  deviceName,
  onAction,
}) => {
  const capability = detectDeviceCapability(device, metrics, childMetrics, childDevices);

  switch (capability) {
    case 'CAMERA':
      return (
        <CameraRenderer
          device={device}
          streamUrl={device.settings?.cameraStreamEndpoint || ''}
          online={online === 1}
          themeColor={themeColor}
        />
      );

    case 'MIXER':
      const powerMetrics = extractPowerMetrics(childMetrics);
      // If no child metrics, check own metrics
      if (powerMetrics.power.length === 0 && metrics.power) {
        powerMetrics.power = metrics.power || [];
        powerMetrics.power_reconstruction = metrics.power_reconstruction || [];
        powerMetrics.energy = metrics.energy || [];
        powerMetrics.state = metrics.state || [];
      }
      return (
        <MixerRenderer
          device={device}
          powerMetrics={powerMetrics}
          online={online}
          deviceName={deviceName || device.name}
          themeColor={themeColor}
        />
      );

    case 'FERMENTOR':
      // Fermentor uses sensor renderer but with child device metrics
      const fermentorMetrics = Object.values(childMetrics).reduce(
        (acc, m) => ({ ...acc, ...m }),
        metrics
      );
      return (
        <SensorRenderer
          device={device}
          metrics={fermentorMetrics}
          online={online}
          deviceName={deviceName || device.name}
          themeColor={themeColor}
        />
      );

    case 'QA':
      // QA station - render camera stream from child camera
      const cameraChild = childDevices.find(
        child => child.deviceTypes?.id === DeviceTypeId.CAMERA
      );
      if (cameraChild) {
        return (
          <CameraRenderer
            device={cameraChild}
            streamUrl={cameraChild.settings?.cameraStreamEndpoint || ''}
            online={online === 1}
            themeColor={themeColor}
          />
        );
      }
      return null;

    case 'SENSOR':
    case 'GENERIC':
    default:
      return (
        <SensorRenderer
          device={device}
          metrics={metrics}
          online={online}
          deviceName={deviceName || device.name}
          themeColor={themeColor}
        />
      );
  }
};

export default DeviceRenderer;
export { detectDeviceCapability };
export type { DeviceCapability, DeviceRendererProps };
